import $ls, { LocalStorageKey } from "@/utils/local-storage";

/**
 * Generates a hash from a string using a simple djb2 algorithm
 */
function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

type DeviceType = "Phone" | "Tablet" | "Desktop";
export type DeviceOS =
  | "iOS"
  | "Android"
  | "Windows"
  | "Mac"
  | "Linux"
  | "Unknown";

function getDeviceType(): DeviceType {
  const ua = navigator.userAgent;
  const isTablet =
    /iPad/.test(ua) ||
    (/Android/.test(ua) && !/Mobile/.test(ua)) ||
    (navigator.maxTouchPoints > 0 &&
      Math.min(screen.width, screen.height) > 600);

  if (isTablet) return "Tablet";

  const isMobile =
    /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/.test(ua);

  if (isMobile) return "Phone";

  return "Desktop";
}

export function getDeviceOS(): DeviceOS {
  const ua = navigator.userAgent;

  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac/.test(ua)) return "Mac";
  if (/Linux/.test(ua)) return "Linux";

  return "Unknown";
}

function getBrowserName(): string {
  const ua = navigator.userAgent;

  if (/CriOS/.test(ua)) return "Chrome";
  if (/FxiOS/.test(ua)) return "Firefox";
  if (/EdgiOS|Edg/.test(ua)) return "Edge";
  if (/OPiOS|OPR|Opera/.test(ua)) return "Opera";
  if (/SamsungBrowser/.test(ua)) return "Samsung";
  if (/Firefox/.test(ua)) return "Firefox";
  if (/Chrome/.test(ua)) return "Chrome";
  if (/Safari/.test(ua)) return "Safari";

  return "Unknown";
}

/**
 * Collects device characteristics to create a unique hash
 */
function collectDeviceCharacteristics(): string {
  const characteristics: string[] = [];

  // Screen properties
  characteristics.push(`${screen.width}x${screen.height}`);
  characteristics.push(`${screen.colorDepth}`);
  characteristics.push(`${window.devicePixelRatio}`);

  // Navigator properties
  characteristics.push(navigator.language);
  characteristics.push(`${navigator.hardwareConcurrency || "unknown"}`);
  characteristics.push(`${(navigator as any).deviceMemory || "unknown"}`);
  characteristics.push(navigator.platform);

  // Timezone
  characteristics.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Canvas fingerprint
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(0, 0, 100, 50);
      ctx.fillStyle = "#069";
      ctx.fillText("DeviceFingerprint", 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText("DeviceFingerprint", 4, 17);
      characteristics.push(canvas.toDataURL());
    }
  } catch {
    characteristics.push("canvas-not-supported");
  }

  // WebGL fingerprint
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl");
    if (gl) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        characteristics.push(
          gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "unknown"
        );
        characteristics.push(
          gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "unknown"
        );
      }
    }
  } catch {
    characteristics.push("webgl-not-supported");
  }

  return characteristics.join("|");
}

/**
 * Generates or retrieves a unique device fingerprint.
 * Format: "OS-Type-Browser-Hash" (e.g., "Android-Tablet-Chrome-a1b2c3d4")
 */
export function getDeviceFingerprint(): string {
  // Check if we already have a stored fingerprint
  const storedFingerprint = $ls.getItem(LocalStorageKey.DEVICE_ID);
  if (storedFingerprint) {
    return storedFingerprint;
  }

  // Generate device info
  const os = getDeviceOS();
  const type = getDeviceType();
  const browser = getBrowserName();

  // Generate unique hash from device characteristics
  const characteristics = collectDeviceCharacteristics();
  const hash = hashString(characteristics);

  // Combine readable info with unique hash
  const fingerprint = `${os}-${type}-${browser}-${hash}`;

  // Store it for future use
  $ls.setItem(LocalStorageKey.DEVICE_ID, fingerprint);

  return fingerprint;
}
